import { useCallback, useEffect, useState } from 'react';
import { useApi } from '../useApi';
import { ErrorNotice } from '../components/ErrorNotice';

// What we think each account owes, against what Stripe is actually billing.
//
// This screen exists because of a specific bug. On 13 September 2026 a tenant
// that was connected and running jobs was billing 0 pairs, and it was found by
// chance while looking at something else. Under-billing is silent by nature —
// nothing errors, an invoice is just smaller than it should be — so the only
// way to catch it is to put the two numbers next to each other and look.

interface Row {
  subscriptionId: string;
  status: string;
  customerName: string | null;
  customerEmail: string | null;
  accountId: string | null;
  stripeQuantity: number;
  ourPairs: number | null;
  matches: boolean | null;
  note: string | null;
}

interface Report {
  rows: Row[];
  mismatches: number;
  unknown: number;
  truncated: boolean;
}

export function ReconciliationPage() {
  const api = useApi();
  const [report, setReport] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!api) return;
    try {
      setReport(await api.get<Report>('/support/reconciliation'));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load reconciliation.');
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!report) {
    return (
      <>
        <ErrorNotice message={error} />
        {!error && <p className="muted">Comparing against Stripe…</p>}
      </>
    );
  }

  return (
    <>
      <ErrorNotice message={error} />

      {report.mismatches > 0 ? (
        <div className="notice error">
          <strong>
            {report.mismatches} account{report.mismatches === 1 ? '' : 's'} bill the wrong
            amount.
          </strong>{' '}
          Our count and Stripe's quantity disagree. Until they match, someone is being
          invoiced for more or fewer instances than they actually have connected.
        </div>
      ) : (
        <div className="notice success">
          Every account matches what Stripe is billing.
        </div>
      )}

      {report.unknown > 0 && (
        <div className="notice warn">
          {report.unknown} subscription{report.unknown === 1 ? '' : 's'} could not be
          checked. <strong>Unknown is not the same as agreeing</strong> — see the note on
          each row.
        </div>
      )}

      <div className="card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Customer</th>
              <th>Status</th>
              <th>Ours</th>
              <th>Stripe</th>
              <th>Agrees</th>
            </tr>
          </thead>
          <tbody>
            {report.rows.map((row) => (
              <tr key={row.subscriptionId}>
                <td>
                  {row.customerName ?? '—'}
                  <br />
                  <span className="muted small">{row.customerEmail ?? row.subscriptionId}</span>
                  {row.note && (
                    <>
                      <br />
                      <span className="muted small">{row.note}</span>
                    </>
                  )}
                </td>
                <td className="small">{row.status}</td>
                <td className="mono">{row.ourPairs ?? '—'}</td>
                <td className="mono">{row.stripeQuantity}</td>
                <td>
                  {row.matches === null ? (
                    <span className="muted">unknown</span>
                  ) : row.matches ? (
                    'yes'
                  ) : (
                    <strong>NO</strong>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {report.truncated && (
          <p className="muted small">Showing the most recent subscriptions only.</p>
        )}
      </div>
    </>
  );
}
